import React, { useState, useEffect, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';

// Configure nunjucks with custom delimiters
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const ApiGatewayField = (
  props: FieldExtensionComponentProps<string>
) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;
  
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  // Get type from ui:options (project or product)
  const type = uiSchema?.['ui:options']?.type || 'project';
  
  // Get component key from ui:options (e.g., 'api_gateway', 'internal_api_gateway', 'external_api_gateway')
  const componentKey = uiSchema?.['ui:options']?.componentKey || 'api_gateway';
  
  // Get project name from ui:options
  const projectNameTemplate = uiSchema?.['ui:options']?.projectName || '';
  const resolvedProjectName = useMemo(() => {
    if (!projectNameTemplate) return '';
    return nunjucks.renderString(projectNameTemplate, formContext.formData || {}).trim();
  }, [projectNameTemplate, formContext.formData]);

  // Normalize the incoming formData: if it's undefined or an empty string, force it to "false".
  const normalized = formData && formData.trim() !== '' ? formData : 'false';

  // Determine the initial checkbox state and radio value
  const initialChecked = normalized !== 'false';
  const initialRadio = initialChecked ? normalized : '';

  // Local state for the checkbox and radio.
  const [checked, setChecked] = useState<boolean>(initialChecked);
  const [radio, setRadio] = useState<string>(initialRadio);
  
  // State for project API Gateway check
  const [loading, setLoading] = useState<boolean>(false);
  const [projectHasShared, setProjectHasShared] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch project details if type is "product"
  useEffect(() => {
    console.log('[ApiGatewayField] Effect triggered', { type, resolvedProjectName, componentKey });
    
    if (type !== 'product' || !resolvedProjectName) {
      console.log('[ApiGatewayField] Skipping fetch - type:', type, 'resolvedProjectName:', resolvedProjectName);
      setProjectHasShared(true); // Default to true for project type
      return;
    }

    const fetchProjectDetails = async () => {
      setLoading(true);
      setFetchError(null);
      
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        
        console.log('[ApiGatewayField] Fetching project details for:', resolvedProjectName, 'componentKey:', componentKey);
        
        const response = await fetch(`${baseUrl}/aws/params`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: `/backstage/project/${resolvedProjectName}` }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch project details: ${response.status}`);
        }

        const data = await response.json();
        const rawValue = data.value || '';
        
        console.log('[ApiGatewayField] Received raw value:', rawValue);
        
        if (rawValue) {
          const parsed = yaml.load(rawValue);
          console.log('[ApiGatewayField] Parsed YAML:', parsed);
          
          if (parsed && typeof parsed === 'object') {
            const components = (parsed as any).components || {};
            console.log('[ApiGatewayField] Components:', components);
            
            const apiGateway = components[componentKey] || 'false';
            console.log('[ApiGatewayField] Component value for', componentKey, ':', apiGateway);
            
            // Check if project has shared API Gateway
            const hasShared = apiGateway === 'shared';
            console.log('[ApiGatewayField] Has shared:', hasShared);
            setProjectHasShared(hasShared);
          }
        }
      } catch (error: any) {
        console.error('Error fetching project details:', error);
        setFetchError(error.message);
        setProjectHasShared(false);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [type, resolvedProjectName, componentKey, discoveryApi, identityApi]);

  // On mount, if the parent value is empty, force it to "false".
  useEffect(() => {
    if (!formData || formData.trim() === '') {
      // Use a timeout to ensure the onChange call happens after mount.
      setTimeout(() => {
        onChange('false');
      }, 0);
    }
  }, [formData, onChange]);

  // Whenever formData changes, update local state.
  useEffect(() => {
    const val = formData && formData.trim() !== '' ? formData : 'false';
    if (val === 'false') {
      setChecked(false);
      setRadio('');
    } else {
      setChecked(true);
      setRadio(val as 'dedicated' | 'shared');
    }
  }, [formData]);

  // Retrieve labels from the schema and uiSchema.
  const checkboxLabel = schema.title || 'API Gateway required';
  const helperText = schema.description || '';
  const radioGroupLabel =
    typeof uiSchema?.['ui:options']?.radioGroupLabel === 'string'
      ? uiSchema['ui:options'].radioGroupLabel
      : 'Select API Gateway Type';

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    setChecked(newChecked);
    if (newChecked) {
      // On checking, default to "shared" if available, otherwise "dedicated"
      const isSharedAvailable = type === 'project' || projectHasShared;
      const newRadio = radio || (isSharedAvailable ? 'shared' : 'dedicated');
      setRadio(newRadio);
      onChange(newRadio);
    } else {
      // On unchecking, clear radio and set value to "false".
      setRadio('');
      onChange('false');
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRadio = event.target.value as 'dedicated' | 'shared';
    setRadio(newRadio);
    onChange(newRadio);
  };

  return (
    <FormControl
      component="fieldset"
      fullWidth
      required={required}
      error={!!rawErrors?.length}
    >
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={handleCheckboxChange} />}
          label={checkboxLabel}
        />
      </FormGroup>
      {checked && (
        <div style={{ marginLeft: '20px' }}>
          <FormLabel component="legend">{radioGroupLabel}</FormLabel>
          {loading && <CircularProgress size={20} />}
          {fetchError && <FormHelperText error>Error: {fetchError}</FormHelperText>}
          <RadioGroup value={radio} onChange={handleRadioChange}>
            {(type === 'project' || projectHasShared) && (
              <>
                <FormControlLabel
                  value="shared"
                  control={<Radio />}
                  label="Shared API gateway (recommended)"
                />
                <FormHelperText style={{ marginLeft: '32px', marginTop: '-4px', marginBottom: '8px' }}>
                  Share the project's API Gateway with other products (cost-effective)
                </FormHelperText>
              </>
            )}
            <FormControlLabel
              value="dedicated"
              control={<Radio />}
              label="Dedicated API gateway"
            />
            <FormHelperText style={{ marginLeft: '32px', marginTop: '-4px', marginBottom: '8px' }}>
              Separate API Gateway for this product only (isolated routing & rate limits)
            </FormHelperText>
          </RadioGroup>
        </div>
      )}
      <FormHelperText>
        {rawErrors && rawErrors.length > 0 ? rawErrors.join(', ') : helperText}
      </FormHelperText>
    </FormControl>
  );
};
